import { useSyncExternalStore, type RefObject } from 'react'
import { getBusySnapshot, subscribeBusy } from '../../api/busy'
import { createFirstOnPage } from '../../lib/firstOnPage'

/**
 * Does the loading surface at `ref` draw the busy line (#392, CAP-O26)?
 *
 * Two things must hold. **The server has refused a read as busy twice** for the
 * same request (`api/busy.ts`: one request's repeated refusals, so a struggling
 * server that still answers some requests cannot flip it back and forth). And
 * **this is the first loading surface on the page in reading order**: the line
 * is about the server, so it is true of every panel still waiting, and said ten
 * times down a page it would look like ten separate faults. It is the same rule
 * the error notes follow for their reassurance, and the same mechanism
 * (`lib/firstOnPage.ts`). A panel that has loaded no longer claims, so the line
 * moves down to the next one still waiting, and is gone when none is.
 *
 * It lives beside `BusyLine` rather than in it only because a component file
 * that also exports a hook is not a Fast Refresh boundary.
 */
const useFirstLoadingSurface = createFirstOnPage()

export function useBusyLine(ref: RefObject<HTMLElement | null>): boolean {
  const first = useFirstLoadingSurface(ref)
  const { busy } = useSyncExternalStore(subscribeBusy, getBusySnapshot)
  return first && busy
}
